import cn from 'classnames';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { useDraftTimelogStore } from '@/features/draftTimelogs';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { isToday } from '@/utils/isToday';

import RenderSkeletonCalendar from './RenderSkeletonCalendar';
import { DAY_NAMES } from './config';
import { useCalendar } from './helpers/useCalendar';
import { CalendarDate } from './ui/CalendarDate';

import s from './TaskCalendar.module.scss';

type TaskCalendarProps = {
  isDropDisabled?: boolean;
};

const TaskCalendar = observer(({ isDropDisabled = false }: TaskCalendarProps) => {
  const { currentDate, filterData: timesheetData, loadingStage } = useTimeLogStore();
  const { filteredDraftsByDate } = useDraftTimelogStore();
  const { isViewingSelf } = useGlobalUserFilterModel();
  const calendar = useCalendar(currentDate);

  const isDayOff = useCallback((day: DateTime) => {
    return day.weekday === 6 || day.weekday === 7;
  }, []);

  if (!calendar) return null;

  const { currentMonth, weeks } = calendar;

  if (loadingStage.isLoading || !timesheetData) return <RenderSkeletonCalendar weeks={weeks} />;

  return (
    <div className={s.calendar}>
      <div className={s.day__names}>
        {DAY_NAMES.map((dayName) => (
          <div
            key={dayName}
            className={cn(s.day__item, {
              [s.day_off]: dayName === 'Сб' || dayName === 'Вс',
            })}
          >
            <p>{dayName}</p>
          </div>
        ))}
      </div>
      <div className={s.calendar__weeks}>
        {weeks.map((week, index) => (
          <div key={index} className={s.calendar__week}>
            {week.map((day, di) => {
              const isCurrentMonth = day.month === currentMonth.month;
              const dateStr = day.toISODate();
              const dayLogs = dateStr ? timesheetData.days[dateStr] || [] : [];
              const dayDrafts = isViewingSelf && dateStr ? filteredDraftsByDate[dateStr] || [] : [];

              return (
                <div
                  key={di}
                  className={cn(s.calendar__day, {
                    [s.calendar__day_current]: isCurrentMonth,
                    [s.day_off]: isDayOff(day) && !isToday(day) && isCurrentMonth,
                    [s.day_today]: isToday(day),
                  })}
                >
                  <CalendarDate
                    day={day}
                    isCurrentMonth={isCurrentMonth}
                    logs={dayLogs}
                    drafts={dayDrafts}
                    isDropDisabled={isDropDisabled}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
});

export default TaskCalendar;
